import { useEffect, useState } from "react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const Shell = ({ title, subtitle, children, testid }) => (
  <div className="min-h-screen bg-white flex flex-col" data-testid={testid}>
    <Navbar />
    <div className="border-b border-foreground bg-foreground text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <span className="label-caps text-accent">Legal</span>
        <h1 className="font-display font-black text-4xl tracking-tight mt-2">{title}</h1>
        {subtitle && <p className="text-white/70 mt-2">{subtitle}</p>}
      </div>
    </div>
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 w-full flex-1 prose-legal space-y-6 text-sm leading-relaxed text-foreground">
      {children}
    </div>
    <Footer />
  </div>
);

const H = ({ children }) => <h2 className="font-display font-bold text-lg tracking-tight mt-6">{children}</h2>;
const P = ({ children }) => <p className="text-muted-foreground">{children}</p>;

const PlaceholderNotice = ({ fields }) => (
  <div className="border-2 border-accent bg-accent/10 p-4" data-testid="placeholder-notice">
    <p className="font-display font-bold text-base text-foreground">⚠ Action required before public launch.</p>
    <p className="text-sm text-muted-foreground mt-1">This is a legally non-binding template. Replace every <code className="bg-muted px-1">[ ]</code> placeholder below with your real, legally valid details:</p>
    <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-foreground list-disc list-inside">
      {fields.map((f) => <li key={f}>{f}</li>)}
    </ul>
  </div>
);

// Renders legal fields from admin settings, falling back to bracket placeholders until filled.
function useLegal() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => setS({}));
  }, []);
  const address = (s && s.legal_business_address) || "[Business address]";
  const email = (s && s.legal_business_email) || "[official business email]";
  const ust = (s && s.legal_ust_id) || "nicht vorhanden / noch nicht erteilt";
  const complete = !!(s && s.legal_business_address && s.legal_business_email);
  return { address, email, ust, complete, ready: s !== null };
}

export function Impressum() {
  const { address, email, ust, complete } = useLegal();
  return (
    <Shell title="Impressum" subtitle="Angaben gemäß § 5 TMG" testid="page-impressum">
      {!complete && <PlaceholderNotice fields={["Business address (Betriebsstätte)", "Official business email", "USt-IdNr (only if provided / applicable)"]} />}
      <H>Diensteanbieter</H>
      <P>
        William Boakye<br />
        Geschäftsbezeichnung: WILTOM Tyres International<br />
        Plattform: WILTOM TradeLink<br />
        {address}<br />
        Telefon: +49 152 18483329<br />
        E-Mail: {email}<br />
        USt-IdNr: {ust}
      </P>
      <H>Geschäftstätigkeit</H>
      <P>Handel mit neuen Reifen, Reifen-Einzelhandel und Großhandel, Onlinehandel sowie Betrieb einer B2B-Plattform für Reifen, Warenbestände und Transportanfragen. Weiterhin: Handel mit und Im- und Export von Reifen.</P>
      <H>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</H>
      <P>William Boakye, Anschrift wie oben.</P>
      <H>Haftungsausschluss</H>
      <P>Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.</P>
    </Shell>
  );
}

export function Datenschutz() {
  const { address, email, complete } = useLegal();
  return (
    <Shell title="Datenschutzerklärung" subtitle="Privacy Policy — how we handle your data" testid="page-datenschutz">
      {!complete && <PlaceholderNotice fields={["Business address (Betriebsstätte)", "Official / privacy contact email", "USt-IdNr (only if provided)"]} />}
      <H>1. Verantwortlicher / Data Controller</H>
      <P>William Boakye, Geschäftsbezeichnung WILTOM Tyres International (Plattform: WILTOM TradeLink), {address}, E-Mail: {email}. We are responsible for the processing of personal data on this platform.</P>
      <H>2. Welche Daten wir verarbeiten / Data we process</H>
      <P>Account data (name, e-mail, role, company), profile data for sellers and transporters, product and job listings, inquiries and quote requests, and technical data (e.g. session tokens) required to operate the service.</P>
      <H>3. Zweck der Verarbeitung / Purpose</H>
      <P>We process your data to provide the marketplace and transport-matching service, to enable communication between buyers, sellers and transporters, for admin verification and moderation, and to keep the platform secure.</P>
      <H>4. Öffentliche Profile / Public profiles</H>
      <P>Transporters listed in the public directory display only safe public information (company name, vehicle types, service areas, ratings, completed jobs and reviews). Documents, insurance files, vehicle registration, private e-mail and private phone numbers are never shown publicly unless you and an administrator explicitly enable contact sharing.</P>
      <H>5. Rechtsgrundlage / Legal basis</H>
      <P>Processing is based on Art. 6(1)(b) GDPR (performance of a contract), Art. 6(1)(f) GDPR (legitimate interests in operating and securing the service) and, where applicable, your consent (Art. 6(1)(a) GDPR).</P>
      <H>6. Ihre Rechte / Your rights</H>
      <P>You have the right to access, rectification, erasure, restriction, data portability and objection regarding your personal data, as well as the right to lodge a complaint with a supervisory authority. To exercise these rights contact {email}.</P>
      <H>7. Speicherdauer / Retention</H>
      <P>We retain personal data only as long as necessary for the purposes described or as required by law.</P>
      <P className="text-xs">Hinweis: Dies ist eine Vorlage und keine Rechtsberatung. Bitte lassen Sie die Datenschutzerklärung vor dem öffentlichen Start rechtlich prüfen.</P>
    </Shell>
  );
}

export function Terms() {
  const { address, email, complete } = useLegal();
  return (
    <Shell title="Terms & Conditions" subtitle="Allgemeine Geschäftsbedingungen (AGB)" testid="page-terms">
      {!complete && <PlaceholderNotice fields={["Business address", "Official business email", "Governing law / jurisdiction"]} />}
      <H>1. Scope</H>
      <P>These terms govern the use of the WILTOM TradeLink platform, which connects buyers, suppliers/sellers, companies posting transport jobs, and verified transporters. Provider: William Boakye, Geschäftsbezeichnung WILTOM Tyres International (Plattform: WILTOM TradeLink), {address}, E-Mail: {email}.</P>
      <H>2. The platform's role</H>
      <P>WILTOM TradeLink is a marketplace and matchmaking platform. We are not a party to the contracts concluded between users. Listings, quotes and agreements are the responsibility of the respective users.</P>
      <H>3. Accounts & verification</H>
      <P>Sellers and transporters require admin approval. Transporters must provide valid documents for verification. We may suspend or block accounts that violate these terms, provide false information or behave unsafely.</P>
      <H>4. Listings & jobs</H>
      <P>Users are responsible for the accuracy and legality of their product listings and transport jobs. Prohibited, counterfeit or unsafe goods are not allowed.</P>
      <H>5. Reviews</H>
      <P>Reviews must be honest and based on a genuine completed job. We may remove reviews that are fraudulent, abusive or violate our policies.</P>
      <H>6. Liability</H>
      <P>To the extent permitted by law, WILTOM TradeLink is not liable for damages arising from transactions, deliveries or communications between users. Mandatory statutory liability remains unaffected.</P>
      <H>7. Changes</H>
      <P>We may update these terms. Continued use after changes constitutes acceptance.</P>
      <P className="text-xs">Note: This is a template and not legal advice. Please have these terms reviewed by a qualified lawyer before public launch.</P>
    </Shell>
  );
}
